import React, { useState, useEffect } from 'react';
import { usePWAInstall } from '../hooks/Usepwainstall';
import BaoModal from './ui/BaoModal';
import Button from './ui/Button';

const DISMISS_KEY = 'pwa_prompt_dismissed_at';
const DISMISS_DAYS = 30;
const SHOW_DELAY_MS = 8000;

function wasDismissedRecently() {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return false;
    const daysSince = (Date.now() - Number(ts)) / (1000 * 60 * 60 * 24);
    return daysSince < DISMISS_DAYS;
}

export default function PWAInstallPrompt({ appName = 'BaoAfrik' }) {
    const { canInstall, isIOS, isInstalled, isInStandaloneMode, triggerInstall } = usePWAInstall();
    const [visible, setVisible] = useState(false);
    const [installing, setInstalling] = useState(false);

    useEffect(() => {
        if (isInStandaloneMode || isInstalled || wasDismissedRecently()) return;

        // Only show if there's something actionable (native prompt or iOS instructions)
        const shouldShow = canInstall || isIOS;
        if (!shouldShow) return;

        const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
        return () => clearTimeout(timer);
    }, [canInstall, isIOS, isInstalled, isInStandaloneMode]);

    const dismiss = () => {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
        setVisible(false);
    };

    const handleInstall = async () => {
        setInstalling(true);
        const accepted = await triggerInstall();
        setInstalling(false);
        if (accepted) setVisible(false);
    };

    return (
        <BaoModal
            isOpen={visible}
            onClose={dismiss}
            panelClassName="relative w-[90%] max-w-sm rounded-[20px] bg-white p-6 shadow-xl text-center mx-auto"
        >
            <button 
                onClick={dismiss} 
                className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors"
                aria-label="Dismiss"
            >
                ✕
            </button>

            <div className="mb-4 flex justify-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-50 text-3xl text-primary-500 shadow-sm border border-primary-100">
                    🌍
                </div>
            </div>

            <h2 className="mb-2 text-xl font-bold text-slate-900 tracking-tight">
                Add {appName} to your home screen
            </h2>
            <p className="mb-6 text-sm text-slate-500 leading-relaxed mx-auto max-w-[280px]">
                Get the full app experience — faster load, offline access, and no browser UI in the way.
            </p>

            {canInstall && (
                <Button
                    onClick={handleInstall}
                    disabled={installing}
                    variant="primary"
                    fullWidth
                    size="md"
                    className="mb-3 font-semibold"
                >
                    {installing ? 'Installing…' : '⬇ Install App'}
                </Button>
            )}

            {isIOS && !canInstall && (
                <div className="mb-4 rounded-xl bg-slate-50 border border-slate-100 p-4 text-left">
                    <p className="mb-3 font-semibold text-sm text-slate-900">To install on iPhone or iPad:</p>
                    <div className="mb-2.5 flex items-center gap-3 text-sm text-slate-700">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-500 text-[11px] font-bold text-white shadow-sm">1</span>
                        <span>Tap the <strong>Share</strong> button <span className="text-lg leading-none">⎋</span></span>
                    </div>
                    <div className="mb-2.5 flex items-center gap-3 text-sm text-slate-700">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-500 text-[11px] font-bold text-white shadow-sm">2</span>
                        <span>Scroll down and tap <strong>Add to Home Screen</strong></span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-700">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-500 text-[11px] font-bold text-white shadow-sm">3</span>
                        <span>Tap <strong>Add</strong> in the top-right</span>
                    </div>
                </div>
            )}

            <button 
                onClick={dismiss} 
                className="w-full rounded-xl border border-slate-200 bg-transparent py-2.5 text-sm font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors"
            >
                Maybe later
            </button>
        </BaoModal>
    );
}